import { useGlobalDialogsStore } from '../global-dialogs.store';
import { GlobalDialogConfig, GlobalDialogType } from '../global-dialogs.store.types';

/**
 * Hook for managing global dialog configuration with type safety.
 *
 * This hook provides access to the configuration object for a specific dialog type,
 * along with a setter function to update the configuration. Configuration is used
 * to store dialog-specific settings that persist across dialog instances.
 *
 * @template T - The specific GlobalDialogType to manage configuration for
 *
 * @param type - The dialog type to get/set configuration for
 *
 * @returns An object containing:
 * - `config`: The current configuration object for the dialog type
 * - `set`: Function to update the configuration (supports partial updates)
 *
 * @example
 * **Basic usage** - Getting and setting dialog configuration:
 * ```tsx
 * const campaignPhoneReachOutConfig = useGlobalDialogConfig(GlobalDialogType.CampaignPhoneReachOut);
 *
 * // Access current configuration
 * const currentConfig = campaignPhoneReachOutConfig.config;
 *
 * // Update configuration (partial update)
 * campaignPhoneReachOutConfig.set({
 *   autoClose: true,
 *   timeout: 5000
 * });
 * ```
 *
 * @example
 * **Component integration** - Using configuration in a dialog component:
 * ```tsx
 * const MyDialogComponent = () => {
 *   const dialogConfig = useGlobalDialogConfig(GlobalDialogType.CampaignPhoneReachOut);
 *
 *   const handleSettingsChange = (newSettings) => {
 *     dialogConfig.set(newSettings);
 *   };
 *
 *   useEffect(() => {
 *     console.log(dialogConfig.config);
 *   }, [dialogConfig.config]);
 *
 *   return null;
 * };
 * ```
 *
 * @remarks
 * **Configuration Persistence**:
 * - Configuration is stored in the global dialogs store and persists across dialog instances
 * - The `set` function performs a shallow merge with existing configuration
 * - Configuration is specific to each dialog type
 *
 * **Type Safety**:
 * - TypeScript ensures that only valid configuration properties can be set
 * - The configuration type is determined by the `GlobalDialogConfigDefinition` mapping
 * - Some dialog types may have `null` configuration (no configurable options)
 *
 * @see {@link useGlobalDialog} for opening and closing dialogs
 * @see {@link useGlobalDialogData} for accessing dialog data
 */
export const useGlobalDialogConfig = <T extends GlobalDialogType>(type: T) => {
  const globalDialogConfig = useGlobalDialogsStore((state) => state.config[type]);

  const setGlobalDialogConfig = useGlobalDialogsStore((state) => state.setGlobalDialogConfig);

  const setConfig = (config: Partial<GlobalDialogConfig<T>>) => {
    setGlobalDialogConfig(type, { ...globalDialogConfig, ...config });
  };

  return { config: globalDialogConfig, set: setConfig };
};
